import { Phone, Mail, MapPin, Clock, MessageCircle, Instagram } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { COMPANY } from "@/data/services";
import { SITE_URL } from "@/config/site";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact — Perspective Assurance Lyon | Devis gratuit & rendez-vous"
        description="Contactez Perspective Assurance à Lyon : devis gratuit, rendez-vous personnalisé, téléphone, WhatsApp. Réponse sous 24h ouvrées."
        canonical={`${SITE_URL}/contact`}
      />

      <section className="bg-cream py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 relative">
          <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Nous contacter</div>
          <h1 className="font-display text-5xl sm:text-6xl text-forest leading-[1.05] tracking-tight max-w-3xl">
            Parlons de <span className="italic text-terracotta">votre projet.</span>
          </h1>
          <p className="mt-6 text-lg text-forest/70 max-w-xl">
            Téléphone, email, WhatsApp ou formulaire — choisissez votre canal préféré. On vous recontacte sous 24h ouvrées.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1 space-y-6">
            <div data-testid="contact-info-phone" className="bg-cream p-6 rounded-2xl border border-soft">
              <Phone className="w-6 h-6 text-terracotta mb-4" />
              <div className="text-xs uppercase tracking-widest text-forest/60 mb-1">Téléphone</div>
              <a href={`tel:${COMPANY.phone}`} className="font-outfit text-lg font-medium text-forest hover:text-terracotta">{COMPANY.phoneDisplay}</a>
            </div>
            <div data-testid="contact-info-email" className="bg-cream p-6 rounded-2xl border border-soft">
              <Mail className="w-6 h-6 text-terracotta mb-4" />
              <div className="text-xs uppercase tracking-widest text-forest/60 mb-1">Email</div>
              <a href={`mailto:${COMPANY.email}`} className="font-outfit text-base font-medium text-forest hover:text-terracotta break-all">{COMPANY.email}</a>
            </div>
            <div data-testid="contact-info-address" className="bg-cream p-6 rounded-2xl border border-soft">
              <MapPin className="w-6 h-6 text-terracotta mb-4" />
              <div className="text-xs uppercase tracking-widest text-forest/60 mb-1">Adresse</div>
              <a href={COMPANY.maps} target="_blank" rel="noopener noreferrer" className="font-outfit font-medium text-forest hover:text-terracotta">{COMPANY.address}</a>
            </div>
            <div data-testid="contact-info-hours" className="bg-cream p-6 rounded-2xl border border-soft">
              <Clock className="w-6 h-6 text-terracotta mb-4" />
              <div className="text-xs uppercase tracking-widest text-forest/60 mb-1">Horaires</div>
              <div className="font-outfit text-forest">Lun – Ven : 9h – 19h</div>
              <div className="font-outfit text-forest/70 text-sm">Sam : 9h – 13h</div>
            </div>
            <div className="flex gap-3">
              <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full py-3 font-outfit font-medium hover:opacity-90">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a href={COMPANY.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="w-12 h-12 rounded-full bg-forest text-white flex items-center justify-center hover:bg-terracotta">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-2 bg-cream p-8 lg:p-12 rounded-3xl border border-soft">
            <h2 className="font-display text-3xl text-forest mb-2">Demande de devis</h2>
            <p className="text-forest/70 mb-8">Remplissez ce formulaire, nous revenons vers vous sous 24h ouvrées.</p>
            <ContactForm source="contact-page" />
          </div>
        </div>
      </section>

      <section className="bg-cream py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-soft">
            <iframe
              title="Carte Perspective Assurance Lyon"
              data-testid="contact-map"
              src="https://www.google.com/maps?q=16+Rue+Cuvier+Lyon+69006&output=embed"
              width="100%"
              height="450"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>
    </>
  );
}
